import React from "react";
import { Card } from "react-bootstrap";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaCamera,
  FaUser,
  FaEye,
  FaVolumeUp,
  FaDesktop,
  FaWifi,
} from "react-icons/fa";
import ViolationModal from "./ViolationModal";
import FinalWarningModal from "./FinalWarningModal";
import WarningToast from "./WarningToast";

function ProctorWidget(props) {
  const {
    proctorOrigin,
    proctorPath = "/",
    token,
    sessionId,
    apiBaseUrl,
    examContext,
    constraints,
    calibrationData,
    onViolation,
    onEnd,
    maxWarnings = 3,
  } = props;

  const iframeRef = React.useRef(null);
  const [isActive, setIsActive] = React.useState(false);
  const [warningCount, setWarningCount] = React.useState(0);
  const [violationCount, setViolationCount] = React.useState(
    examContext?.proctorResumeState?.totalViolationCount || 0
  );
  const [warnings, setWarnings] = React.useState({
    "eyes-off-target": 0,
    "head-off-screen": 0,
    "multiple-faces": 0,
  });
  const [violations, setViolations] = React.useState({
    "no-face": 0,
    "multiple-faces": 0,
    "eyes-off-target": 0,
    "head-off-screen": 0,
    "tab-switched": 0,
    "tab-hidden": 0,
  });

  // Dynamic thresholds
  const [warningsPerViolation, setWarningsPerViolation] = React.useState(() => {
    const cfg = examContext?.warningCount;
    return typeof cfg === 'number' && cfg > 0 ? cfg : 3;
  });

  const effectiveMaxViolations =
    typeof examContext?.violationCount === 'number' && examContext.violationCount > 0
      ? examContext.violationCount
      : maxWarnings;

  // Modal state
  const [showViolationModal, setShowViolationModal] = React.useState(false);
  const [currentViolation, setCurrentViolation] = React.useState(null);
  const [showFinalWarning, setShowFinalWarning] = React.useState(false);
  const [toastQueue, setToastQueue] = React.useState([]);

  const toastTimersRef = React.useRef([]);

  const removeToast = React.useCallback((id) => {
    setToastQueue((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = React.useCallback((toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const duration = toast.duration ?? (toast.type === 'violation' ? 5000 : 3500);

    setToastQueue((prev) => [...prev, { id, ...toast }]);

    const timerId = setTimeout(() => {
      removeToast(id);
    }, duration);

    toastTimersRef.current.push(timerId);
  }, [removeToast]);

  React.useEffect(() => {
    return () => {
      toastTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      toastTimersRef.current = [];
    };
  }, []);

  // Restore violation count from proctorResumeState when resuming assessment
  React.useEffect(() => {
    if (examContext?.proctorResumeState?.totalViolationCount !== undefined) {
      const resumeCount = examContext.proctorResumeState.totalViolationCount;
      if (resumeCount > 0) {
        setViolationCount(resumeCount);
        console.log('[ProctorWidget] Restored violation count from resume state:', resumeCount);
      }
    }
  }, [examContext?.proctorResumeState?.totalViolationCount]);

  const [hasStopRequested, setHasStopRequested] = React.useState(false);

  const sendStopMessageToIframe = React.useCallback((source = 'unknown') => {
    if (!iframeRef.current?.contentWindow) {
      console.warn('[ProctorWidget] Unable to send proctor:stop – iframe window missing', { source });
      return;
    }

    try {
      iframeRef.current.contentWindow.postMessage(
        { type: 'proctor:stop', reason: source },
        proctorOrigin
      );
      console.log('[ProctorWidget] proctor:stop message sent to iframe', { source });
    } catch (error) {
      console.error('[ProctorWidget] Failed to send stop message to iframe:', error);
    }
  }, [proctorOrigin]);

  const notifyParentOnEnd = React.useCallback((source = 'unknown') => {
    if (typeof onEnd === 'function') {
      try {
        console.log('[ProctorWidget] Notifying parent onEnd callback', { source });
        onEnd();
      } catch (error) {
        console.error('[ProctorWidget] Error calling onEnd callback:', error);
      }
    } else {
      console.error('[ProctorWidget] onEnd is not a function:', typeof onEnd);
    }
  }, [onEnd]);

  const requestStopAndSubmit = React.useCallback(
    (source = 'unknown') => {
      if (hasStopRequested) {
        console.log('[ProctorWidget] Stop already requested, ignoring duplicate', { source });
        return;
      }
      setHasStopRequested(true);
      console.log('[ProctorWidget] Stop requested', { source });
      sendStopMessageToIframe(source);
      notifyParentOnEnd(source);
    },
    [hasStopRequested, sendStopMessageToIframe, notifyParentOnEnd]
  );

  // Cleanup: stop proctoring iframe when component unmounts
  React.useEffect(() => {
    return () => {
      console.log('[ProctorWidget] Component unmounting, stopping proctoring iframe');
      sendStopMessageToIframe('unmount');
    };
  }, [sendStopMessageToIframe]);
  
  // Monitoring status state
  const [monitoringStatus, setMonitoringStatus] = React.useState({
    faceDetection: "ok", // ok, warning, error
    singlePerson: "ok",
    eyeTracking: "ok",
    audioLevel: "ok",
    tabFocus: "ok",
    netConnectivity: "ok",
  });

  React.useEffect(() => {
    function onMessage(e) {
      // if (e.origin !== proctorOrigin) {
      //   console.warn('[ProctorWidget] ⚠️ Message rejected - origin mismatch:', {
      //     received: e.origin,
      //     expected: proctorOrigin,
      //     messageType: e.data?.type,
      //   });
      //   return;
      // }
      const { type, payload } = e.data || {};

      if (type === 'proctor:stopped') {
        console.log('[ProctorWidget] iframe confirmed proctor:stop, marking inactive');
        setIsActive(false);
      }

      if (type === "proctor:ready") {
        console.log("[ProctorWidget] ✅ Proctoring active - iframe ready", {
          origin: e.origin,
          sessionId,
        });
        setIsActive(true);
      }

      // Warning cleared (user corrected behavior)
      if (type === "proctor:warning-cleared") {
        const reason = payload?.reason;
        console.log("[ProctorWidget] Warning cleared:", reason);

        // Reset warnings for this type
        setWarnings((prev) => ({
          ...prev,
          [reason]: 0,
        }));

        // Reset monitoring status back to OK
        setMonitoringStatus((prev) => ({
          ...prev,
          faceDetection: reason === "no-face" ? "ok" : prev.faceDetection,
          singlePerson: reason === "multiple-faces" ? "ok" : prev.singlePerson,
          eyeTracking: reason === "eyes-off-target" ? "ok" : prev.eyeTracking,
          tabFocus: reason === "head-off-screen" ? "ok" : prev.tabFocus,
        }));
      }

      // Warning issued (not yet a violation - accumulating warnings)
      if (type === "proctor:warning-issued") {
        const reason = payload?.reason;
        const count = payload?.warningCount || 0;
        const threshold = payload?.threshold || 3;

        console.log("[ProctorWidget] Warning issued:", {
          reason,
          count,
          threshold,
        });

        // Track current warnings-per-violation threshold
        if (threshold && threshold > 0) {
          setWarningsPerViolation(threshold);
        }

        // Update warnings for this violation type
        setWarnings((prev) => ({
          ...prev,
          [reason]: count,
        }));

        addToast({
          type: 'warning',
          reason,
          count,
          threshold,
        });
        
        // Update monitoring status to show warning
        setMonitoringStatus((prev) => ({
          ...prev,
          faceDetection: reason === "no-face" ? "warning" : prev.faceDetection,
          singlePerson:
            reason === "multiple-faces" ? "warning" : prev.singlePerson,
          eyeTracking:
            reason === "eyes-off-target" ? "warning" : prev.eyeTracking,
          tabFocus: reason === "head-off-screen" ? "warning" : prev.tabFocus,
        }));

        // Don't auto-reset status for warnings - keep showing until cleared or escalated
      }

      // Violation confirmed (either 3 warnings accumulated or immediate violation)
      if (type === "proctor:warning") {
        const reason = payload?.reason;
        const count = payload?.count || 0; // This is violation count from backend
        const isImmediate = payload?.isImmediate || false;

        console.log("[ProctorWidget] ========= VIOLATION RECEIVED =========", {
          reason,
          violationCount: count,
          isImmediate,
          payload,
        });

        // IMPORTANT: Use the maximum of current count and backend count
        // This prevents count from going backwards when resuming (backend resets to 0)
        setViolationCount((prevCount) => {
          // If backend count is less than or equal to current, it means backend reset
          // In that case, increment from previous count (prevCount + 1 for new violation)
          // Otherwise, backend is sending absolute total, use it
          const newCount = count <= prevCount ? prevCount + 1 : count;
          console.log("[ProctorWidget] Violation count update:", {
            previous: prevCount,
            backendCount: count,
            newCount,
            reason: count <= prevCount ? 'incremented (backend reset)' : 'using backend total',
          });
          return newCount;
        });

        // Also update per-type violation counts
        setViolations((prev) => {
          const newViolations = {
            ...prev,
            [reason]: (prev[reason] || 0) + 1,
          };
          console.log(
            "[ProctorWidget] Per-type violations updated:",
            newViolations
          );
          return newViolations;
        });

        // Reset warnings for this type (violation triggered, warnings reset)
        if (!isImmediate) {
          setWarnings((prev) => ({
            ...prev,
            [reason]: 0,
          }));
        }

        // Check if max violations reached - show final warning modal
        console.log("[ProctorWidget] Checking max violations:", {
          count,
          maxWarnings: effectiveMaxViolations,
        });
        if (count >= effectiveMaxViolations) {
          console.log(
            "[ProctorWidget] ✅ Max violations reached, showing final warning modal"
          );
          setShowFinalWarning(true);
        } else {
          // Show regular violation modal
          console.log(
            "[ProctorWidget] Showing regular violation modal (",
            count,
            "of",
            effectiveMaxViolations,
            ")"
          );
          setCurrentViolation({ type: reason, count, isImmediate });
          setShowViolationModal(true);
        }

        // Update monitoring status based on violation type
        setMonitoringStatus((prev) => ({
          ...prev,
          faceDetection: reason === "no-face" ? "error" : prev.faceDetection,
          singlePerson:
            reason === "multiple-faces" ? "error" : prev.singlePerson,
          eyeTracking:
            reason === "eyes-off-target" ? "error" : prev.eyeTracking,
          tabFocus:
            reason === "tab-switched" || reason === "tab-hidden"
              ? "error"
              : prev.tabFocus,
        }));

        // Reset status after a delay
        setTimeout(() => {
          setMonitoringStatus((prev) => ({
            ...prev,
            faceDetection: reason === "no-face" ? "ok" : prev.faceDetection,
            singlePerson:
              reason === "multiple-faces" ? "ok" : prev.singlePerson,
            eyeTracking: reason === "eyes-off-target" ? "ok" : prev.eyeTracking,
            tabFocus:
              reason === "tab-switched" || reason === "tab-hidden"
                ? "ok"
                : prev.tabFocus,
          }));
        }, 3000);

        // Call parent callback
        if (onViolation) {
          onViolation(reason, count);
        }

        addToast({
          type: isImmediate ? 'violation-immediate' : 'violation',
          reason,
          count,
          threshold: effectiveMaxViolations,
        });
      }
      
      if (type === 'proctor:end') {
        console.warn('[ProctorWidget] Proctoring ended by server – propagated event received');
        setIsActive(false);
        requestStopAndSubmit('server-proctor-end');
      }

      // Listen for metrics updates
      if (type === "proctor:metrics") {
        // Update real-time status based on metrics
        const { faces, gazeInZone } = payload || {};
        setMonitoringStatus((prev) => ({
          ...prev,
          faceDetection:
            faces === 1 ? "ok" : faces === 0 ? "warning" : "warning",
          singlePerson: faces === 1 ? "ok" : faces > 1 ? "warning" : "ok",
          eyeTracking: gazeInZone ? "ok" : "warning",
        }));
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [proctorOrigin, onViolation, requestStopAndSubmit]);

  const onLoad = React.useCallback(() => {
    const payload = {
      apiBaseUrl,
      token,
      sessionId,
      examContext,
      constraints,
      calibrationData,
      mode: "monitor",
    };
    console.log('[ProctorWidget] 📤 Sending init message to proctor-spa iframe:', {
      proctorOrigin,
      apiBaseUrl,
      sessionId,
      examId: examContext?.testId,
      hasToken: !!token,
      iframeSrc: `${proctorOrigin}/`,
    });
    iframeRef.current?.contentWindow?.postMessage(
      { type: "proctor:init", payload },
      proctorOrigin
    );
  }, [
    apiBaseUrl,
    token,
    sessionId,
    examContext,
    constraints,
    calibrationData,
    proctorOrigin,
  ]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "ok":
        return <FaCheckCircle style={{ color: "#10b981", fontSize: 14 }} />;
      case "warning":
        return (
          <FaExclamationTriangle style={{ color: "#f59e0b", fontSize: 14 }} />
        );
      case "error":
        return <FaTimesCircle style={{ color: "#ef4444", fontSize: 14 }} />;
      default:
        return <FaCheckCircle style={{ color: "#9ca3af", fontSize: 14 }} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "ok":
        return "Ok";
      case "warning":
        return "Warning";
      case "error":
        return "Lost";
      default:
        return "Unknown";
    }
  };

  return (
    <>
    {/* Warning Toast for active warnings */}
    <WarningToast toasts={toastQueue} />
    
    <Card style={{ width: '100%', maxWidth: 320, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <Card.Body style={{ padding: 16 }}>
        {/* Camera Feed Container */}
        <div style={{ 
          position: 'relative',
          width: '100%',
          paddingBottom: '75%', // 4:3 aspect ratio (240/320 = 0.75)
          background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
          borderRadius: 8,
          marginBottom: 16,
          overflow: 'hidden',
        }}>
          {/* Iframe with camera feed - absolute positioned to fill container */}
          <iframe
            ref={iframeRef}
            src={`${proctorOrigin}${proctorPath}`}
            onLoad={onLoad}
            allow="camera; microphone"
            sandbox="allow-scripts allow-same-origin"
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
          
          {/* Loading placeholder - shows until active */}
          {!isActive && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
              zIndex: 1,
            }}>
              <FaCamera style={{ fontSize: 48, color: '#000', opacity: 0.3 }} />
            </div>
          )}
          
          {/* Active Indicator Overlay */}
          {isActive && (
            <div style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: '#10b981',
              boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
              animation: 'pulse 2s infinite',
              zIndex: 2,
            }} />
          )}
        </div>
        
        {/* Proctoring Active Status */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 8,
          marginBottom: 20,
        }}>
          <FaCheckCircle style={{ color: '#10b981', fontSize: 16 }} />
          <span style={{ color: '#10b981', fontWeight: 600, fontSize: 14 }}>
            Proctoring Active
          </span>
        </div>

          {/* Monitoring Status Section */}
          <div style={{ marginBottom: 20 }}>
            <h6
              style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 12,
                color: "#1f2937",
              }}
            >
              Monitoring Status
            </h6>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Face Detection */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaCamera style={{ color: "#6b7280", fontSize: 14 }} />
                <span style={{ flex: 1, fontSize: 13, color: "#4b5563" }}>
                  Face Detection
                </span>
                {getStatusIcon(monitoringStatus.faceDetection)}
                <span
                  style={{
                    fontSize: 12,
                    color:
                      monitoringStatus.faceDetection === "ok"
                        ? "#10b981"
                        : "#ef4444",
                    minWidth: 45,
                  }}
                >
                  {getStatusText(monitoringStatus.faceDetection)}
                </span>
              </div>

              {/* Single Person */}
                {/* <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FaUser style={{ color: "#6b7280", fontSize: 14 }} />
                  <span style={{ flex: 1, fontSize: 13, color: "#4b5563" }}>
                    Single Person
                  </span>
                  {getStatusIcon(monitoringStatus.singlePerson)}
                  <span
                    style={{
                      fontSize: 12,
                      color:
                        monitoringStatus.singlePerson === "ok"
                          ? "#10b981"
                          : "#ef4444",
                      minWidth: 45,
                    }}
                  >
                    {getStatusText(monitoringStatus.singlePerson)}
                  </span>
                </div> */}

              {/* Eye Tracking */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaEye style={{ color: "#6b7280", fontSize: 14 }} />
                <span style={{ flex: 1, fontSize: 13, color: "#4b5563" }}>
                  Eye Tracking
                </span>
                {getStatusIcon(monitoringStatus.eyeTracking)}
                <span
                  style={{
                    fontSize: 12,
                    color:
                      monitoringStatus.eyeTracking === "ok"
                        ? "#10b981"
                        : "#f59e0b",
                    minWidth: 45,
                  }}
                >
                  {getStatusText(monitoringStatus.eyeTracking)}
                </span>
              </div>

              {/* Audio Level */}
              {/* <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaVolumeUp style={{ color: "#6b7280", fontSize: 14 }} />
                <span style={{ flex: 1, fontSize: 13, color: "#4b5563" }}>
                  Audio Level
                </span>
                {getStatusIcon(monitoringStatus.audioLevel)}
                <span style={{ fontSize: 12, color: "#10b981", minWidth: 45 }}>
                  Ok
                </span>
              </div> */}

              {/* Tab Focus */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaDesktop style={{ color: "#6b7280", fontSize: 14 }} />
                <span style={{ flex: 1, fontSize: 13, color: "#4b5563" }}>
                  Tab Focus
                </span>
                {getStatusIcon(monitoringStatus.tabFocus)}
                <span
                  style={{
                    fontSize: 12,
                    color:
                      monitoringStatus.tabFocus === "ok"
                        ? "#10b981"
                        : "#ef4444",
                    minWidth: 45,
                  }}
                >
                  {getStatusText(monitoringStatus.tabFocus)}
                </span>
              </div>

              {/* Net Connectivity */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FaWifi style={{ color: "#6b7280", fontSize: 14 }} />
                <span style={{ flex: 1, fontSize: 13, color: "#4b5563" }}>
                  Net Connectivity
                </span>
                {getStatusIcon(monitoringStatus.netConnectivity)}
                <span style={{ fontSize: 12, color: "#10b981", minWidth: 45 }}>
                  Stable
                </span>
              </div>
            </div>
          </div>

          {/* Active Warnings Section */}
          {Object.entries(warnings).some(([_, count]) => count > 0) && (
            <div style={{ marginBottom: 16 }}>
              <h6
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#1f2937",
                }}
              >
                Active Warnings
              </h6>

              {Object.entries(warnings).map(
                ([type, count]) =>
                  count > 0 && (
                    <div
                      key={type}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "#fef3c7",
                        border: "1px solid #fbbf24",
                        borderRadius: 6,
                        padding: "8px 12px",
                        marginBottom: 8,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#92400e",
                          }}
                        >
                          {type.replace(/-/g, " ")}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#78350f",
                            marginTop: 2,
                          }}
                        >
                          Warning {count} of {warningsPerViolation}
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#fbbf24",
                          color: "#fff",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        {count}
                      </div>
                    </div>
                  )
              )}
            </div>
          )}

          {/* Violations Section */}
          <div>
            <h6
              style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 8,
                color: "#1f2937",
              }}
            >
              Violations
            </h6>

            <p
              style={{
                fontSize: 13,
                color: "#6b7280",
                marginBottom: 10,
              }}
            >
              {violationCount} of {effectiveMaxViolations} violations
              <br />
              <span
                style={{ fontSize: 11, fontStyle: "italic", color: "#f97316" }}
              >
                ({warningsPerViolation} warnings = 1 violation)
              </span>
            </p>

            {/* Visual Progress Bar */}
            <div
              style={{
                display: "flex",
                gap: 4,
                marginBottom: 12,
              }}
            >
              {[...Array(effectiveMaxViolations)].map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    background: idx < violationCount ? "#f97316" : "#e5e7eb",
                    transition: "background 0.3s",
                  }}
                />
              ))}
            </div>

            {/* Violation Breakdown (optional, can be toggled) */}
            {violationCount > 0 && (
              <details style={{ marginTop: 12 }}>
                <summary
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  View violation details
                </summary>
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: "1px solid #e5e7eb",
                    fontSize: 11,
                    color: "#6b7280",
                  }}
                >
                  {Object.entries(violations).map(
                    ([type, count]) =>
                      count > 0 && (
                        <div
                          key={type}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 4,
                          }}
                        >
                          <span>{type.replace(/-/g, " ")}</span>
                          <span style={{ fontWeight: 600 }}>{count}x</span>
                        </div>
                      )
                  )}
                </div>
              </details>
            )}
          </div>
        </Card.Body>

        {/* CSS Animation for pulse effect */}
        <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }
      `}</style>
      </Card>

      {/* Violation Modal */}
      <ViolationModal
        show={showViolationModal}
        onHide={() => setShowViolationModal(false)}
        violation={currentViolation}
      />

      {/* Final Warning Modal - Test Auto-Submit */}
      <FinalWarningModal
        show={showFinalWarning}
        warningCount={violationCount}
        maxWarnings={effectiveMaxViolations}
        onConfirm={() => {
          setShowFinalWarning(false);
          // Trigger test end after user acknowledges - stop proctoring first, then call onEnd
          console.log("[ProctorWidget] Final warning confirmed, stopping proctoring");
          requestStopAndSubmit('final-warning');
        }}
      />
    </>
  );
}

export default ProctorWidget;
